import React from 'react';
import MockTeamRoster from '../00-shared/MockTeamRoster';
import WorkflowDiagram from '../00-shared/WorkflowDiagram';
import MockTeamTrendChart from '../00-shared/MockTeamTrendChart';

interface ForTeamsSectionProps {
    highlightColor: {
        primary: string;
        hover: string;
        gradient: {
            from: string;
            to: string;
            fromLight: string;
            toGray: string;
        };
    };
}

const featureVisualizations: React.ReactNode[] = [
    <MockTeamRoster variant="compact" className="py-2" />,
    <WorkflowDiagram variant="compact" className="py-2" />,
    <div className="border border-neutral-700 p-4">
        <div className="flex items-center gap-4 mb-3">
            <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 bg-[#3B82F6]" />
                <span className="text-[10px] text-gray-400">Team Avg</span>
            </div>
            <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 bg-[#3B82F6] opacity-30" />
                <span className="text-[10px] text-gray-400">Team Range</span>
            </div>
        </div>
        <MockTeamTrendChart variant="compact" />
    </div>,
];

const ForTeamsSection: React.FC<ForTeamsSectionProps> = ({ highlightColor }) => {
    const features = [
        {
            title: 'Team Roster at a Glance',
            desc: 'See every athlete\'s readiness, HRV, and status in one unified roster view. Instantly identify who is ready to train and who needs attention.',
        },
        {
            title: 'Automated Workflows',
            desc: 'Automate reports and readiness alerts that fit your team\'s training schedule.',
        },
        {
            title: 'Team-wide Trends',
            desc: 'Track how your entire roster trends over weeks. See team averages, variance, and spot outliers before they become problems.',
        }
    ];

    return (
        <section className="pt-32 pb-24 px-4 sm:px-6 lg:px-8 bg-black relative notebook-grid overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-white/10 to-transparent pointer-events-none"></div>
            <div className="max-w-7xl mx-auto relative z-10">
                <div className="max-w-6xl mx-auto relative z-10 text-center pb-12 border-b border-neutral-800">
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-8 observe-fade opacity-0">
                        Performance Solutions for Every Team
                    </h1>
                    <p className="text-gray-300 max-w-3xl mx-auto observe-fade opacity-0">
                        From collegiate athletics to professional sports, CGM Sports provides intelligent performance solutions tailored to your team's needs.
                    </p>
                </div>
                <div className="mt-12 mb-12 observe-fade opacity-0 text-center">
                    <div className="flex items-center justify-center mb-4">
                        <div className="w-full max-w-[120px]">
                            <svg viewBox="0 0 80 60" className="w-full h-auto" fill="none">
                                {/* Browser chrome */}
                                <rect x="1" y="1" width="78" height="58" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
                                <rect x="1" y="1" width="78" height="10" fill="rgba(255,255,255,0.06)" />
                                <circle cx="8" cy="6" r="2" fill="rgba(255,255,255,0.2)" />
                                <circle cx="14" cy="6" r="2" fill="rgba(255,255,255,0.2)" />
                                {/* Radar chart inside */}
                                <polygon points="40,16 58,25 54,45 26,45 22,25" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" />
                                <polygon points="40,22 52,28 49,42 31,42 28,28" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" />
                                <line x1="40" y1="16" x2="40" y2="32" stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" />
                                <line x1="58" y1="25" x2="40" y2="32" stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" />
                                <line x1="54" y1="45" x2="40" y2="32" stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" />
                                <line x1="26" y1="45" x2="40" y2="32" stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" />
                                <line x1="22" y1="25" x2="40" y2="32" stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" />
                                <polygon points="40,18 56,27 50,44 28,41 24,26" stroke="#3B82F6" strokeWidth="1" fill="rgba(59,130,246,0.1)" />
                            </svg>
                        </div>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Sports Programs</h2>
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                        <span className="text-white">
                            Power Your Team's Edge
                        </span>
                    </h3>
                    <h4 className="text-xl md:text-2xl font-semibold text-gray-300 mb-6">
                        Your Intelligent Performance Layer
                    </h4>
                    <p className="text-lg text-gray-300 leading-relaxed max-w-3xl mx-auto">
                        Our platform connects data from multiple wearable devices and learns from your team's performance patterns. Get intelligent insights that help coaches optimize training and keep athletes performing at their best.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                    {features.map((feature, index) => (
                        <div key={index} className="bg-neutral-900 observe-fade opacity-0 text-center flex flex-col grid-accent" style={{ animationDelay: `${index * 0.2}s` }}>
                            <div className="p-4 border-b border-neutral-800 bg-neutral-900/50">
                                {featureVisualizations[index]}
                            </div>
                            <div className="p-6">
                                <h6 className="text-lg font-bold text-white mb-2">{feature.title}</h6>
                                <p className="text-gray-300">{feature.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default ForTeamsSection;

